/**
 * File Preview Utilities
 * Handles file reading and preview generation for supported file types
 */

export interface FilePreviewData {
  type: 'pdf' | 'image' | 'text' | 'doc' | 'unsupported';
  content?: string;
  url?: string;
  error?: string;
}

/**
 * Get file type based on file extension and MIME type
 */
export const getFileType = (file: File): FilePreviewData['type'] => {
  const extension = file.name.toLowerCase().split('.').pop();
  const mimeType = file.type.toLowerCase();

  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'pdf';
  }
  
  if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
    return 'image';
  }
  
  if (mimeType === 'text/plain' || extension === 'txt') {
    return 'text';
  }
  
  if (mimeType.includes('word') || ['doc', 'docx'].includes(extension || '')) {
    return 'doc';
  }
  
  return 'unsupported';
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file icon based on file type
 */
export const getFileIcon = (file: File): string => {
  const fileType = getFileType(file);
  
  switch (fileType) {
    case 'pdf':
      return '📄';
    case 'image':
      return '🖼️';
    case 'text':
      return '📝';
    case 'doc':
      return '📋';
    default:
      return '📁';
  }
};

/**
 * Generate preview data for a file
 */
export const generateFilePreview = async (file: File): Promise<FilePreviewData> => {
  const fileType = getFileType(file);
  
  try {
    switch (fileType) {
      case 'pdf':
        return {
          type: 'pdf',
          url: URL.createObjectURL(file)
        };
        
      case 'image':
        return {
          type: 'image',
          url: URL.createObjectURL(file)
        };
        
      case 'text':
        const textContent = await readTextFile(file);
        return {
          type: 'text',
          content: textContent.substring(0, 1000) + (textContent.length > 1000 ? '...' : '')
        };
        
      case 'doc':
        // For DOC files, we'll show basic info since we can't easily preview them
        return {
          type: 'doc',
          content: `Microsoft Word Document\nFile: ${file.name}\nSize: ${formatFileSize(file.size)}`
        };
        
      default:
        return {
          type: 'unsupported',
          content: `File type not supported for preview\nFile: ${file.name}\nSize: ${formatFileSize(file.size)}`
        };
    }
  } catch (error) {
    return {
      type: 'unsupported',
      error: `Error reading file: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

/**
 * Read text file content
 */
const readTextFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target?.result as string || '');
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Clean up object URLs to prevent memory leaks
 */
export const cleanupObjectURL = (url: string): void => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};
